import { VolumeIcon } from '@/components/media/volume-icon';
import { CHTooltip } from '@/components/tooltip';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { MediaControls } from '@/hooks/use-media-controls/types';
import { useSmoothedTime } from '@/hooks/use-smoothed-time';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import { SECOND_MS } from '@calm/web-common/dist/time';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    Fade,
    IconButton,
    Slider,
    SxProps,
    Theme,
    Typography,
    alpha,
} from '@rhg/libs/material';
import {
    Back15,
    ClosedCaptionFilled,
    ClosedCaptionOutline,
    ExpandScreenFilled,
    MinimizeScreenFilled,
    Forward15,
    PauseCtaFilled,
    PlayCtaFilled,
    InfoFilled,
} from '@rhg/libs/material/icons';
import {
    responsivePaddingDefault,
    styleMaxLines,
    sxAppend,
} from '@rhg/libs/material/sx';
import { displayTime } from '@rhg/utils/media';
import { ReactElement, useState } from 'react';
import { useIntl } from 'react-intl';
import { MediaSubtitles } from '../../subtitles';
import { ControlButtonType } from '../types';

const styleVideoControlsOverlay = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
};

const styleBottomContainer = {
    position: 'absolute',
    bottom: -1,
    left: 0,
    width: '100%',
};

// Mirror the Minimal player's controls scrim: a gradient fading from
// transparent at the top to solid black at the bottom, rather than a flat
// fill. Because it backs this container (which starts at the title/transport
// row), the gradient rises exactly to the top of the controls bar and never
// reaches the closed captions, which render as a sibling above it.
const styleVideoControlsContainer = {
    background: `linear-gradient(180deg, ${alpha(DS_COLOR.WHITE_100P, 0)} 0%, ${
        DS_COLOR.BLACK_100P
    } 100%)`,
    width: '100%',
    py: 4,
    px: (theme: Theme) => ({
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
};

const styleTimingContainer = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

const styleTimingSlider = {
    width: '100%',
    mx: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};
// Compact viewports drop the inline labels, so the scrubber spans the full
// width edge-to-edge and aligns with the stacked time row beneath it.
const styleTimingSliderCompact = { mx: 0 };
const styleTimingControl = {
    width: '100%',
    // MUI's own SliderThumb/SliderTrack bake in a `left/width/bottom/height`
    // transition unconditionally. useSmoothedTime repositions the thumb every
    // animation frame (~60-100Hz) — far faster than that transition can
    // settle — so each frame restarts it before it paints, freezing the
    // thumb in place. Disable it; JS now owns the motion. EVE-13651.
    '& .MuiSlider-thumb': {
        transition: 'none',
    },
    '& .MuiSlider-track': {
        transition: 'none',
    },
};

const styleNonMobileElement = {
    display: { xs: 'none', lg: 'block' },
};

const styleTitleSubtitle = { maxWidth: '27%' };

// Centred row beneath the scrubber holding CC · favorite · mute. Shaped like
// the audio player's `styleBottomActionsRow`. On wide viewports the top margin
// reuses `CONTENT` so the gap below the scrubber matches the gap above it (the
// transport row's `mb` on `styleControlsMain`); below `md` it tightens to 16 to
// keep the stacked time row and the buttons grouped.
const styleActionsRow = {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    mt: (theme: Theme) => ({ xs: 4, md: theme.ds.spacingUnits.CONTENT }),
};

const styleControlsMain = {
    width: '100%',
    display: 'flex',
    justifyContent: {
        xs: 'center',
        sm: 'center',
        md: 'center',
        lg: 'space-between',
    },
    alignItems: 'center',
    position: 'relative',
    minHeight: '48px',
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

const styleControlsCenter = {
    width: '100%',
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
};

const styleFullScreenButtonContainer = {
    display: 'flex',
    zIndex: (theme: Theme) => theme.zIndex.modal,
    position: 'absolute',
    top: 0,
    right: 0,
    p: responsivePaddingDefault,
};

const styleFullScreenButton = {
    borderRadius: '50%',
    backgroundColor: (theme: Theme) => theme.palette.mask.black['60p'],
    color: 'action.active',
};

const styleFullScreenButtonFullScreen = {
    color: 'primary.main',
};

const styleSubtitles = {
    mb: (theme: Theme) => ({
        xs: 14 + theme.ds.spacingUnits.CONTENT,
        sm: 14 + theme.ds.spacingUnits.CONTENT,
        md: 14 + theme.ds.spacingUnits.SECTION,
    }),
};

// When the controls overlay hides, the subtitles drop toward the bottom but
// keep a section's worth of spacing plus the viewport's side padding (the same
// breakpoints as `responsivePaddingDefault`), since there's no controls bar
// left to clear.
const styleSubtitlesControlsHidden = {
    mb: (theme: Theme) => ({
        xs: theme.ds.spacingUnits.SECTION + theme.ds.spacingUnits.SIDE_SMALL,
        sm: theme.ds.spacingUnits.SECTION + theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SECTION + theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SECTION + theme.ds.spacingUnits.SIDE_LARGE,
    }),
};

export interface VideoControlsProps {
    controls: MediaControls;
    actionButton?: ReactElement;
    title?: string;
    subtitle?: string;
    sx?: SxProps<Theme>;
    hasAutoPlayRestriction?: boolean;
    onControlClick?: (buttonType: ControlButtonType) => void;
    areControlsHidden?: boolean;
}
export function VideoControls({
    controls,
    actionButton,
    title,
    subtitle,
    sx,
    onControlClick,
    hasAutoPlayRestriction,
    areControlsHidden = false,
}: VideoControlsProps) {
    const { formatMessage } = useIntl();
    const [isTooltipOpen, setIsTooltipOpen] = useState(true);
    const isCompact = useIsMobile();

    const {
        isFullscreen,
        isMuted,
        isShowingSubtitles,
        isPaused,
        currentCue,
        timingState,
        togglePlay,
        toggleSubtitles,
        toggleFullscreen,
        toggleMuted,
        setTime,
        playbackRate,
    } = controls;

    // Interpolates between the ~4Hz ticks so the thumb moves smoothly, while
    // still snapping instantly on seek/track-change. EVE-13651.
    const smoothedCurrentTime = useSmoothedTime({
        currentTime: timingState.currentTime,
        isPaused: timingState.isPaused,
        playbackRate,
        duration: timingState.duration,
    });

    const handleTogglePlay = () => {
        if (isPaused && hasAutoPlayRestriction) {
            setIsTooltipOpen(false);
        }
        if (onControlClick) {
            // The reverse of the current state, prior to calling togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }

        togglePlay();
    };

    return (
        <Box sx={sxAppend(styleVideoControlsOverlay, sx)}>
            <Box sx={styleFullScreenButtonContainer}>
                <IconButton
                    sx={sxAppend(
                        styleFullScreenButton,
                        styleFullScreenButtonFullScreen
                    )}
                    size="medium"
                    trackButton="VideoPlayerToggleFullscreen"
                    aria-label={formatMessage(
                        playerMessages['player.toggle_fullscreen']
                    )}
                    onClick={() => toggleFullscreen()}
                >
                    {isFullscreen ? (
                        <MinimizeScreenFilled />
                    ) : (
                        <ExpandScreenFilled />
                    )}
                </IconButton>
            </Box>
            <Box sx={styleBottomContainer}>
                {isShowingSubtitles && (
                    <MediaSubtitles
                        cue={currentCue}
                        isMuted={isMuted}
                        sx={sxAppend(
                            styleSubtitles,
                            areControlsHidden && styleSubtitlesControlsHidden
                        )}
                    />
                )}
                <Fade in={!areControlsHidden} unmountOnExit>
                    <Box sx={styleVideoControlsContainer}>
                        <Box sx={styleControlsMain}>
                            <Box
                                sx={sxAppend(
                                    styleNonMobileElement,
                                    styleTitleSubtitle
                                )}
                            >
                                <Typography
                                    variant="h6"
                                    component="p"
                                    color="text.body"
                                    sx={styleMaxLines(2)}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    variant="subtitle2"
                                    component="p"
                                    color="text.sub"
                                    sx={styleMaxLines(2)}
                                >
                                    {subtitle}
                                </Typography>
                            </Box>
                            <Box
                                sx={styleControlsCenter}
                                data-testid="video-controls-transport"
                            >
                                <IconButton
                                    trackButton="VideoPlayerBack15"
                                    aria-label={formatMessage(
                                        playerMessages['player.backward_15']
                                    )}
                                    onClick={() =>
                                        setTime(
                                            Math.max(
                                                timingState.currentTime - 15,
                                                0
                                            )
                                        )
                                    }
                                    size="playerControl"
                                >
                                    <Back15 />
                                </IconButton>
                                {hasAutoPlayRestriction ? (
                                    <CHTooltip
                                        isOpen={isTooltipOpen}
                                        startIcon={<InfoFilled />}
                                        topLabel={formatMessage(
                                            playerMessages[
                                                'player.autoplay_error.top_label'
                                            ]
                                        )}
                                        content={formatMessage(
                                            playerMessages[
                                                'player.autoplay_error.content'
                                            ]
                                        )}
                                    >
                                        <IconButton
                                            trackButton={
                                                timingState.isPaused
                                                    ? 'VideoPlayerPlay'
                                                    : 'VideoPlayerPause'
                                            }
                                            onClick={handleTogglePlay}
                                            aria-label={
                                                timingState.isPaused
                                                    ? formatMessage(
                                                          playerMessages[
                                                              'player.play'
                                                          ]
                                                      )
                                                    : formatMessage(
                                                          playerMessages[
                                                              'player.pause'
                                                          ]
                                                      )
                                            }
                                            size="largePlayerControl"
                                        >
                                            {timingState.isPaused ? (
                                                <PlayCtaFilled />
                                            ) : (
                                                <PauseCtaFilled />
                                            )}
                                        </IconButton>
                                    </CHTooltip>
                                ) : (
                                    <IconButton
                                        trackButton={
                                            timingState.isPaused
                                                ? 'VideoPlayerPlay'
                                                : 'VideoPlayerPause'
                                        }
                                        onClick={handleTogglePlay}
                                        aria-label={
                                            timingState.isPaused
                                                ? formatMessage(
                                                      playerMessages[
                                                          'player.play'
                                                      ]
                                                  )
                                                : formatMessage(
                                                      playerMessages[
                                                          'player.pause'
                                                      ]
                                                  )
                                        }
                                        size="largePlayerControl"
                                    >
                                        {timingState.isPaused ? (
                                            <PlayCtaFilled />
                                        ) : (
                                            <PauseCtaFilled />
                                        )}
                                    </IconButton>
                                )}
                                <IconButton
                                    trackButton="VideoPlayerForward15"
                                    aria-label={formatMessage(
                                        playerMessages['player.forward_15']
                                    )}
                                    onClick={() =>
                                        setTime(
                                            Math.min(
                                                timingState.currentTime + 15,
                                                timingState.duration
                                            )
                                        )
                                    }
                                    size="playerControl"
                                >
                                    <Forward15 />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={styleTimingContainer}>
                            {!isCompact && (
                                <Typography
                                    variant="subtitle2"
                                    color="text.body"
                                    component="div"
                                    aria-label={formatMessage(
                                        playerMessages['player.current_time'],
                                        {
                                            time: displayTime(
                                                timingState.currentTime
                                            ),
                                        }
                                    )}
                                >
                                    {displayTime(timingState.currentTime)}
                                </Typography>
                            )}
                            <Box
                                sx={sxAppend(
                                    styleTimingSlider,
                                    isCompact && styleTimingSliderCompact
                                )}
                            >
                                <Slider
                                    sx={styleTimingControl}
                                    value={smoothedCurrentTime}
                                    max={timingState.duration}
                                    aria-label={formatMessage(
                                        playerMessages['player.seek_slider']
                                    )}
                                    getAriaValueText={(sliderValue) =>
                                        formatMessage(
                                            playerMessages[
                                                'player.current_time'
                                            ],
                                            {
                                                time: displayTime(sliderValue),
                                            }
                                        )
                                    }
                                    step={timingState.duration / SECOND_MS}
                                    size="small"
                                    onChangeCommitted={(_, newTime) => {
                                        setTime(newTime as number);
                                    }}
                                />
                            </Box>
                            {!isCompact && (
                                <Typography
                                    variant="subtitle2"
                                    color="text.body"
                                    component="div"
                                    aria-label={formatMessage(
                                        playerMessages['player.duration'],
                                        {
                                            time: displayTime(
                                                timingState.duration
                                            ),
                                        }
                                    )}
                                >
                                    {displayTime(timingState.duration)}
                                </Typography>
                            )}
                        </Box>
                        {isCompact && (
                            <Box
                                sx={styleTimingContainer}
                                data-testid="video-controls-timing-stacked"
                            >
                                <Typography
                                    variant="subtitle2"
                                    color="text.body"
                                    component="div"
                                    aria-label={formatMessage(
                                        playerMessages['player.current_time'],
                                        {
                                            time: displayTime(
                                                timingState.currentTime
                                            ),
                                        }
                                    )}
                                >
                                    {displayTime(timingState.currentTime)}
                                </Typography>
                                <Typography
                                    variant="subtitle2"
                                    color="text.body"
                                    component="div"
                                    aria-label={formatMessage(
                                        playerMessages['player.duration'],
                                        {
                                            time: displayTime(
                                                timingState.duration
                                            ),
                                        }
                                    )}
                                >
                                    {displayTime(timingState.duration)}
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={styleActionsRow}
                            data-testid="video-controls-actions"
                        >
                            <IconButton
                                trackButton={
                                    isShowingSubtitles
                                        ? 'VideoPlayerDisableCC'
                                        : 'VideoPlayerEnableCC'
                                }
                                onClick={() => toggleSubtitles()}
                                aria-label={
                                    isShowingSubtitles
                                        ? formatMessage(
                                              playerMessages[
                                                  'player.disable_cc'
                                              ]
                                          )
                                        : formatMessage(
                                              playerMessages['player.enable_cc']
                                          )
                                }
                                size="medium"
                                variant="contained"
                            >
                                {isShowingSubtitles ? (
                                    <ClosedCaptionFilled color="info" />
                                ) : (
                                    <ClosedCaptionOutline color="inherit" />
                                )}
                            </IconButton>
                            {actionButton}
                            <IconButton
                                trackButton={
                                    isMuted
                                        ? 'VideoPlayerUnmute'
                                        : 'VideoPlayerMute'
                                }
                                onClick={() => toggleMuted()}
                                size="medium"
                                variant="contained"
                                aria-label={
                                    isMuted
                                        ? formatMessage(
                                              playerMessages['player.unmute']
                                          )
                                        : formatMessage(
                                              playerMessages['player.mute']
                                          )
                                }
                            >
                                <VolumeIcon isMuted={isMuted} />
                            </IconButton>
                        </Box>
                    </Box>
                </Fade>
            </Box>
        </Box>
    );
}
